import type { ReactNode } from 'react';
import { PageHead } from 'ui/kit/PageHead';
import { useLocation, useNavigate, useParams } from 'react-router';

import { Award, BookOpen, Gift, GraduationCap, MapPin, ShieldCheck, Star, UserRound, Video, Wallet } from 'lucide-react';
import { Button } from 'ui';
import { RichAboutContent } from 'ui/RichAboutContent';
import { hasAboutContent } from 'utils/aboutHtml';

import screen from '../StudentTaskScreen.module.scss';
import classes from './DetailsRep.module.scss';
import teacherIcon from '../../../assets/icons/Header/Group 27.svg';

import { usePublicProfile } from 'hooks/usePublicProfile';
import { useCreateProposal } from 'hooks/useCreateProposal';
import type { TutorSearchItem } from 'api/v1/requests/bulletins';
import { sexProfileLabel } from 'utils/sexLabels';
import { parsePositiveInt } from 'utils/parsePositiveInt';
import { TutorReviewsSection } from 'modules/Teacher/components/TutorReviewsSection';
import { TutorReviewsSummary } from 'modules/Teacher/components/TutorReviewsSummary';
import { BrandLoader } from 'ui/BrandLoader/BrandLoader';
import { DetailsMenuSlot } from 'modules/Details/DetailsMenu/DetailsMenu';

const pickEduYear = (edu: Record<string, unknown>, ...keys: string[]): string | null => {
  for (const key of keys) {
    const v = edu?.[key];
    if (v == null || v === '') continue;
    return String(v);
  }
  return null;
};

const geoCityLabel = (geo: unknown): string | null => {
  if (!geo || typeof geo !== 'object') return null;
  const g = geo as Record<string, unknown>;
  const city =
    (typeof g.city === 'string' && g.city.trim()) ||
    (typeof g.name === 'string' && g.name.trim()) ||
    (typeof g.city_name === 'string' && g.city_name.trim()) ||
    null;
  return city || null;
};

// StudentView is passed by the router; layout does not branch on it yet.
export const DetailsRepProfile = (_props: { StudentView?: boolean }) => {
  const { repId, id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const searchItem = location.state as TutorSearchItem | undefined;
  const canInvite = searchItem?.can_invite !== false;
  const inviteHint =
    !canInvite
      ? searchItem?.invite_block_reason ||
        'Репетитор пока не может взять заявки этого типа.'
      : null;

  const { data, isLoading } = usePublicProfile(parsePositiveInt(repId) ?? 0);
  const { mutate: sendProposal, isPending } = useCreateProposal();

  if (isLoading) {
    return <BrandLoader center label='Загрузка...' />;
  }

  if (!data) {
    return <div>Профиль не найден</div>;
  }

  const handlePropose = (tutorProfileId: number) => {
    const bulletinId = parsePositiveInt(id);
    if (!bulletinId) return;
    if (!canInvite) {
      return;
    }

    sendProposal({
      bulletinId,
      data: {
        tutor_profile_id: tutorProfileId,
        message:
          'Здравствуйте, мне понравился ваш профиль, хотел бы обсудить с вами занятия',
      },
    });
  };

  const fullname = `${data?.surname || ''} ${data?.name || ''} ${data?.patronymic || ''}`.trim();

  // Prefer same-origin /v1/... so cookies/auth work; keep absolute https as-is.
  const rawPhoto = (data?.profile_photo_url || '').trim();
  let photoPath = teacherIcon;
  if (rawPhoto) {
    if (rawPhoto.startsWith('http://') || rawPhoto.startsWith('https://')) {
      try {
        const u = new URL(rawPhoto);
        photoPath = u.pathname.startsWith('/v1/') ? u.pathname + u.search : rawPhoto;
      } catch {
        photoPath = rawPhoto;
      }
    } else {
      photoPath = rawPhoto.startsWith('/') ? rawPhoto : `/${rawPhoto}`;
    }
  }

  const educations = data?.teacher_educations || [];
  const prices = data?.teacher_prices || [];

  const skillsFromProfile = (data?.list_skills || []).filter(Boolean) as string[];
  const subjectsFromPrices = Array.from(
    new Set<string>(
      prices
        .flatMap((p: any) => [
          p?.category_title || p?.category_name,
          p?.subcategory_title || p?.subcategory_name,
        ])
        .map((s: unknown) => (typeof s === 'string' ? s.trim() : ''))
        .filter(Boolean),
    ),
  );
  const skills = skillsFromProfile.length > 0 ? skillsFromProfile : subjectsFromPrices;

  const aboutHtml = hasAboutContent(data?.about)
    ? data.about
    : hasAboutContent(data?.description)
      ? data.description
      : null;

  const formatParts: string[] = [];
  if (data?.format_online) formatParts.push('Онлайн');
  if (data?.format_offline) formatParts.push('Офлайн');
  if (data?.mode && data.mode !== 'any' && formatParts.length === 0) {
    formatParts.push(data.mode === 'online' ? 'Онлайн' : data.mode === 'offline' ? 'Офлайн' : data.mode);
  }

  const trustLabel = data?.trust_level
    ? String(data.trust_level).charAt(0).toUpperCase() + String(data.trust_level).slice(1)
    : null;
  const city = geoCityLabel(data?.geo_range);
  const ratingValue =
    typeof data?.review_avg === 'number'
      ? data.review_avg
      : typeof data?.rating === 'number'
        ? data.rating
        : null;

  const formatLine = formatParts.join(' / ') || 'Не указан';
  const educationLine = educations.length
    ? educations
        .map((edu: Record<string, unknown>) => {
          const parts = [edu.university, edu.faculty, edu.specialization].filter(Boolean);
          return parts.join(' / ');
        })
        .filter(Boolean)
        .join('; ')
    : '';

  const metaChips: { key: string; icon: ReactNode; text: string }[] = [];
  const sexText = sexProfileLabel(data?.sex);
  if (sexText) metaChips.push({ key: 'sex', icon: <UserRound size={16} aria-hidden />, text: sexText });
  if (city) metaChips.push({ key: 'city', icon: <MapPin size={16} aria-hidden />, text: city });
  if (formatParts.length > 0)
    metaChips.push({ key: 'fmt', icon: <Video size={16} aria-hidden />, text: formatParts.join(' / ') });
  metaChips.push({
    key: 'rating',
    icon: <Star size={16} aria-hidden />,
    text:
      ratingValue != null && ratingValue > 0
        ? `Рейтинг ${Number(ratingValue).toFixed(1)}${
            typeof data?.total_reviews === 'number' && data.total_reviews > 0 ? ` (${data.total_reviews})` : ''
          }`
        : 'Рейтинг не указан',
  });
  if (trustLabel)
    metaChips.push({ key: 'trust', icon: <Award size={16} aria-hidden />, text: `Уровень ${trustLabel}` });
  metaChips.push({
    key: 'trial',
    icon: <Gift size={16} aria-hidden />,
    text: data?.trial_lesson ? 'Пробное занятие' : 'Без пробного занятия',
  });

  return (
    <main className={screen.page}>
      <PageHead crumb={<>Личный кабинет / Мои задачи</>} title={<>Анкета репетитора</>} />
      <DetailsMenuSlot />
      <section className={`${screen.card} ${classes.fxCard}`}>
        <header className={classes.fxHead}>
          <div className={classes.fxPhoto}>
            <img
              src={photoPath}
              alt={fullname || 'Репетитор'}
              onError={(e) => {
                e.currentTarget.src = teacherIcon;
              }}
            />
          </div>
          <div className={classes.fxHeadBody}>
            <h2 className={classes.fxName}>{fullname || 'Репетитор'}</h2>
            <div className={classes.fxBadges}>
              <span className={screen.badge}>Анкета репетитора</span>
              {!!data?.profile_verified && (
                <span className={classes.fxVerified}>
                  <ShieldCheck size={16} aria-hidden />
                  Документы подтверждены
                </span>
              )}
            </div>
            {data?.id_user ? (
              <TutorReviewsSummary tutorUserId={data.id_user} publicAccess className={classes.fxSummary} />
            ) : null}
          </div>
        </header>

        <ul className={classes.fxChips}>
          {metaChips.map((c) => (
            <li key={c.key} className={classes.fxChip}>
              {c.icon}
              <span>{c.text}</span>
            </li>
          ))}
        </ul>

        <div className={screen.row}>
          <div className={screen.field}>
            <p className={screen.fieldLabel}>Образование</p>
            <p className={screen.fieldValue}>{educationLine || 'Не указано'}</p>
          </div>
          <div className={screen.field}>
            <p className={screen.fieldLabel}>Формат</p>
            <p className={screen.fieldValue}>{formatLine}</p>
          </div>
        </div>

        {educations.length > 1 && (
          <section className={classes.fxSection}>
            <h3 className={classes.fxLabel}>
              <GraduationCap size={16} aria-hidden /> Образование подробно
            </h3>
            <ul className={classes.fxList}>
              {educations.map((edu: any, index: number) => {
                const ys = pickEduYear(edu, 'startYear', 'year_start', 'start_year');
                const ye = pickEduYear(edu, 'endYear', 'year_end', 'end_year');
                const years = ys && ye ? `${ys}-${ye}` : ye || ys || null;
                const parts = [edu?.university, edu?.faculty, edu?.specialization, years].filter(Boolean);
                return <li key={edu?.item_id || index}>{parts.join(', ')}</li>;
              })}
            </ul>
          </section>
        )}

        {skills.length > 0 && (
          <section className={classes.fxSection}>
            <h3 className={classes.fxLabel}>
              <BookOpen size={16} aria-hidden /> Навыки и предметы
            </h3>
            <div className={classes.fxTags}>
              {skills.map((skill: string) => (
                <span key={skill}>{skill}</span>
              ))}
            </div>
          </section>
        )}

        {aboutHtml && (
          <section className={classes.fxSection}>
            <h3 className={classes.fxLabel}>
              <UserRound size={16} aria-hidden /> Подход и о себе
            </h3>
            <div className={classes.fxAbout}>
              <RichAboutContent content={aboutHtml} />
            </div>
          </section>
        )}

        {prices.length > 0 && (
          <section className={classes.fxSection}>
            <h3 className={classes.fxLabel}>
              <Wallet size={16} aria-hidden /> Услуги и цены
            </h3>
            <ul className={classes.fxServices}>
              {prices.map((row: any, i: number) => {
                const cat = row?.category_title || row?.category_name || '';
                const sub = row?.subcategory_title || row?.subcategory_name || '';
                const price = row?.price;
                return (
                  <li key={row?.item_id || i} className={classes.fxService}>
                    <span className={classes.fxServiceText}>
                      <b>{sub || cat || 'Услуга'}</b>
                      {sub && cat ? <small>{cat}</small> : null}
                    </span>
                    <span className={classes.fxPrice}>
                      {price != null && price !== '' ? `${price} руб. / час` : 'Цена не указана'}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {data?.id_user ? (
          <section className={`${classes.fxSection} ${classes.fxReviews}`}>
            <TutorReviewsSection tutorUserId={data.id_user} publicAccess />
          </section>
        ) : null}

        <p className={`${screen.note} ${classes.fxNote}`}>
          Чат доступен после отклика. Комментарии - после договоренности; отзывы проходят модерацию.
        </p>
        <div className={`${screen.actions} ${classes.fxActions}`}>
          <Button
            onClick={() => handlePropose(data.id!)}
            variant={'filled'}
            size={'md'}
            color={'primary'}
            shape={'brick'}
            loading={isPending}
            disabled={isPending || !canInvite}
          >
            Выбрать и открыть чат
          </Button>
          <button
            type='button'
            className={screen.ghost}
            onClick={() => id && navigate(`/profile/details/${id}/search`)}
          >
            К репетиторам
          </button>
        </div>
        {inviteHint ? <p className={classes['hire-invite-hint']}>{inviteHint}</p> : null}
      </section>
    </main>
  );
};
